import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Api } from '../lib/api';
import { useUi } from '../store/ui';
import { qk } from './useVault';

function norm(s: string): string {
  return s.normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ').replace(/\.md$/, '');
}

/**
 * Resolve a wikilink target ([[Title]], [[path/Title]], [[Title#Heading]]) to a note
 * and open it. If nothing matches, offer to create it.
 */
export function useOpenLink() {
  const openNote = useUi((s) => s.openNote);
  const toast = useUi((s) => s.toast);
  const qc = useQueryClient();

  return useCallback(
    async (target: string, opts: { split?: boolean; create?: boolean } = {}) => {
      const [notePart, heading] = target.split('#');
      const name = notePart.split('|')[0].trim();
      if (!name) return;
      const key = norm(name);
      const hits = await Api.notes.titles(name, 20);
      const exact =
        hits.find((h) => norm(h.title) === key) ??
        hits.find((h) => norm(h.path) === key || norm(h.path.split('/').pop() ?? '') === key) ??
        hits.find((h) => h.alias && norm(h.alias) === key);
      if (exact) {
        openNote(exact.id, { split: opts.split });
        if (heading) {
          // Preview scrolls to heading ids; the editor searches for it.
          setTimeout(() => document.dispatchEvent(new CustomEvent('cortex:goto-heading', { detail: { noteId: exact.id, heading } })), 150);
        }
        return;
      }
      if (opts.create !== false && window.confirm(`No note called "${name}". Create it?`)) {
        const created = await Api.notes.create({ title: name, type: 'note' });
        qc.invalidateQueries({ queryKey: qk.tree });
        toast(`Created ${created.path}`);
        openNote(created.id, { split: opts.split });
      }
    },
    [openNote, toast, qc],
  );
}
